// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
    Button,
    ContentLoader,
    Dropdown,
    Heading,
    Icon,
    Item,
    Label,
    Link,
    ListAction,
    Menu,
    Paragraph,
    Text,
    TextArea,
    Toolbar,
    ToolbarExpander,
    useTranslate,
} from '@plesk/ui-library';
import { Locale, Tooltip } from 'jsw';

import { Answer, CopilotContext } from '../../CopilotContext';
import AiStylePicker, { AiStyle } from './AiStylePicker';
import PopoverConfirm from './PopoverConfirm';
import MessageItem from 'components/layouts/PageLayout/PageHeader/Copilot/MessageItem';
import parseJsonChunks from './parseJsonChunks';
import { useMutation, useQuery } from '@apollo/client';
import COPILOT_CHAT_QUERY from 'queries/CopilotChat.graphql';
import SEND_COPILOT_FEEDBACK from 'queries/SendCopilotFeedback.graphql';

enum Role {
    User = 'user',
    Assistant = 'assistant',
}

const SUGGESTIONS = [
    { key: 'welcome.suggest.domain', icon: 'globe' },
    { key: 'welcome.suggest.upload-files', icon: 'arrow-up-tray' },
    { key: 'welcome.suggest.wordpress', icon: 'wordpress' },
    { key: 'welcome.suggest.security', icon: 'shield' },
    { key: 'welcome.suggest.email-phone', icon: 'mail' },
    { key: 'welcome.suggest.spam', icon: 'mail-circle-filled' },
    { key: 'welcome.suggest.slow-website', icon: 'monitoring' },
] as const;

const getErrorMessage = (status: number, translate: ReturnType<typeof useTranslate>): string | ReactNode[] => {
    switch (status) {
        case 400:
            return translate('session-expired');
        default:
            return translate('service-unavailable');
    }
};

const STREAM_OBJECT_DELAY_MS = 10;
const Translate = Locale.getTranslate('components.copilot');

const Chat = () => {
    const translate = useTranslate('components.copilot');
    const {
        setIsRightSidebarOpen,
        isRightSidebarOpen,
        loading: historyLoading,
        answers, setAnswers,
        clearHistory,
        pendingQuery,
        setPendingQuery,
    } = useContext(CopilotContext);

    const { data: { config } = {} } = useQuery(COPILOT_CHAT_QUERY);


    const isRightSidebarOpenRef = useRef(isRightSidebarOpen);

    const [question, setQuestion] = useState('');
    const [selectedStyle, setSelectedStyle] = useState<AiStyle | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLFormElement>(null);

    const [loading, setLoading] = useState(false);
    const nextIdRef = useRef(0);
    useEffect(() => {
        nextIdRef.current = answers.reduce((max, a) => Math.max(max, a.id + 1), 0);
    }, [answers]);

    const [feedbackDisabled, setFeedbackDisabled] = useState<Set<Answer['id']>>(new Set());
    const [sendFeedback] = useMutation(SEND_COPILOT_FEEDBACK);

    const setAnswersAndFocus = (newAnswers: Answer[]) => {
        setAnswers(newAnswers);
        setTimeout(() => {
            scrollToBottom();
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        });
    };

    const scrollToBottom = () => {
        const listEl = listRef.current;
        if (listEl) {
            listEl.scrollTop = listEl.scrollHeight;
        }
    };

    // Scroll to the bottom of the chat on initial render.
    // ResizeObserver is used instead of a simple scrollToBottom() call
    // because content (Markdown, fonts, icons) renders asynchronously
    useEffect(() => {
        const listEl = listRef.current;
        if (!listEl) {
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout>;
        const observer = new ResizeObserver(() => {
            listEl.scrollTop = listEl.scrollHeight;
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => observer.disconnect(), 100);
        });
        observer.observe(listEl);

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (isRightSidebarOpen && !isRightSidebarOpenRef.current) {
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        }
        isRightSidebarOpenRef.current = isRightSidebarOpen;
    }, [isRightSidebarOpen]);

    useEffect(() => {
        const container = listRef.current;
        if (!container) {
            return;
        }

        const showTooltip = (e: Event) => {
            const el = e.target as HTMLElement;
            if (el.tagName === 'A' && el.hasAttribute('data-title')) {
                const text = el.getAttribute('data-title');
                if (!text) {
                    return;
                }
                Tooltip.init(el, { text });
                Tooltip.set({ element: el, config: { text } });
            }
        };

        container.addEventListener('focus', showTooltip, true);
        container.addEventListener('mouseenter', showTooltip, true);

        return () => {
            container.removeEventListener('focus', showTooltip, true);
            container.removeEventListener('mouseenter', showTooltip, true);
        };
    }, []);

    const getErrorResponse = (status: number): Response => {
        if (status === 400) {
            window.location.reload();
        }
        return new Response(
            JSON.stringify({ error: getErrorMessage(status, translate) }), {
                status,
                headers: { 'Content-Type': 'application/json' },
            });
    };

    const getAnswerResponse = async (query: string): Promise<Response> => {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };
        const tokenEl = document.getElementById('forgery_protection_token') as HTMLMetaElement | null;
        if (tokenEl) {
            headers['X-Forgery-Protection-Token'] = tokenEl.content;
        }
        setLoading(true);
        setTimeout(() => scrollToBottom());
        try {
            const resp = await fetch('/admin/copilot/gen-answer', {
                method: 'POST',
                body: JSON.stringify(selectedStyle ? { query, style: selectedStyle } : { query }),
                headers,
                credentials: 'same-origin',
            });
            if (!resp.ok) {
                return getErrorResponse(resp.status);
            }
            return resp;
        } catch {
            return getErrorResponse(503);
        }
    };

    const processNonStreamResponse = async (
        resp: Response,
        newAnswers: Answer[],
    ) => {
        const respData = await resp.json();
        const answersWithAnswer: Answer[] = [
            ...newAnswers,
            {
                role: Role.Assistant,
                content: respData?.answer,
                error: respData?.error,
                id: nextIdRef.current++,
                chat_id: respData?.chat_id,
                message_id: respData?.message_id,
                able_to_answer: respData?.able_to_answer,
            },
        ];
        setAnswersAndFocus(answersWithAnswer);
        setLoading(false);
    };

    const processStreamResponse = async (
        resp: Response,
        newAnswers: Answer[],
    ) => {
        const reader = resp.body?.getReader();
        if (!reader) {
            setLoading(false);
            return;
        }
        const decoder = new TextDecoder('utf-8');
        let lastAnswer = newAnswers.length > 0 ? newAnswers[newAnswers.length - 1] : undefined;
        let noResponse = true;
        let buffer = '';
        let loadingCleared = false;

        while (true) {
            const { value, done } = await reader.read();
            if (done) {
                break;
            }
            const chunk = decoder.decode(value, { stream: true });

            const { objects: chunkObjects, buffer: newBuffer } = parseJsonChunks(chunk, buffer);
            buffer = newBuffer;
            if (chunkObjects.length > 0) {
                noResponse = false;
            }

            const typing = !document.hidden;
            let needsUpdate = false;

            for (const chunkObject of chunkObjects) {
                const messageContent: string = chunkObject.message.content;

                if (messageContent === '') {
                    continue;
                }

                if (lastAnswer && lastAnswer.role === Role.Assistant) {
                    lastAnswer = {
                        ...lastAnswer,
                        content: (lastAnswer.content ?? '') + messageContent,
                    };
                    newAnswers[newAnswers.length - 1] = lastAnswer;
                } else {
                    lastAnswer = {
                        role: Role.Assistant,
                        content: messageContent,
                        id: nextIdRef.current++,
                        chat_id: chunkObject.chat_id,
                        message_id: chunkObject.message_id,
                        able_to_answer: chunkObject.able_to_answer,
                        streaming: true,
                    };
                    newAnswers.push(lastAnswer);
                }

                if (typing) {
                    setAnswersAndFocus([...newAnswers]);
                    if (!loadingCleared) {
                        setLoading(false);
                        loadingCleared = true;
                    }
                    // A short delay for smoother text streaming in the UI
                    await new Promise(resolve => setTimeout(resolve, STREAM_OBJECT_DELAY_MS));
                } else {
                    needsUpdate = true;
                }
            }

            if (!typing && needsUpdate) {
                setAnswersAndFocus([...newAnswers]);
                if (!loadingCleared) {
                    setLoading(false);
                    loadingCleared = true;
                }
            }
        }
        if (noResponse) {
            newAnswers.push({
                role: Role.Assistant,
                error: String(translate('service-unavailable')),
                id: nextIdRef.current++,
            });
            setAnswersAndFocus([...newAnswers]);
        } else {
            const last = newAnswers[newAnswers.length - 1];
            if (last?.role === Role.Assistant) {
                newAnswers[newAnswers.length - 1] = { ...last, streaming: false };
                setAnswersAndFocus([...newAnswers]);
            }
        }
        setLoading(false);
    };

    const loadAnswer = async (askQuestion = question) => {
        const trimmed = askQuestion.trim();
        if (!trimmed || loading) {
            return;
        }
        const newAnswers: Answer[] = [...answers, { role: Role.User, content: trimmed, id: nextIdRef.current++ }];
        setAnswers(newAnswers);
        setTimeout(() => scrollToBottom());

        setQuestion('');

        const resp = await getAnswerResponse(trimmed);

        const contentType = resp.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
            await processNonStreamResponse(resp, newAnswers);
        } else {
            await processStreamResponse(resp, newAnswers);
        }
    };


    const loadAnswerRef = useRef(loadAnswer);
    loadAnswerRef.current = loadAnswer;

    useEffect(() => {
        if (pendingQuery) {
            loadAnswerRef.current(pendingQuery);
            setPendingQuery(null);
        }
    }, [pendingQuery, setPendingQuery]);

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        loadAnswer();
    };

    const headerMenu = (
        <Menu>
            <ListAction icon="recycle" onClick={clearHistory}>
                {({ wrap }) => (
                    <PopoverConfirm target={wrap(translate('clear-all'))}>
                        {translate('confirm-clear-all-question')}
                    </PopoverConfirm>
                )}
            </ListAction>
        </Menu>
    );

    const handleFeedback = async (messageId: Answer['id'], value: Answer['feedback']) => {
        const targetAnswer = answers.find(a => a.id === messageId);
        if (targetAnswer?.message_id == null || targetAnswer?.chat_id == null) {
            return;
        }

        setFeedbackDisabled(prev => new Set(prev).add(messageId));
        setTimeout(() => {
            setFeedbackDisabled(prev => {
                const next = new Set(prev);
                next.delete(messageId);
                return next;
            });
        }, 3000);

        const initialFeedbackValue = targetAnswer.feedback;
        const isToggleOff = initialFeedbackValue === value;
        const newValue = isToggleOff ? undefined : value;

        updateAnswerFeedback(messageId, newValue);

        try {
            await sendFeedback({
                variables: {
                    input: {
                        chatId: targetAnswer.chat_id,
                        messageId: targetAnswer.message_id,
                        feedback: newValue,
                    },
                },
            });
        } catch {
            rollbackAnswerFeedback(messageId, newValue, initialFeedbackValue);
        }
    };

    const updateAnswerFeedback = (messageId: Answer['id'], value: Answer['feedback']) => {
        setAnswers(prev => prev.map(answer => {
            if (answer.id !== messageId) {
                return answer;
            }
            return { ...answer, feedback: value };
        }));
    };

    const rollbackAnswerFeedback = (
        messageId: Answer['id'],
        expectedCurrentValue: Answer['feedback'],
        rollbackValue: Answer['feedback'],
    ) => {
        setAnswers(prev => prev.map(answer => {
            if (answer.id !== messageId || answer.feedback !== expectedCurrentValue) {
                return answer;
            }
            return { ...answer, feedback: rollbackValue };
        }));
    };

    return (
        <div className="plesk-copilot-chat-container">
            <div className="plesk-copilot-chat-header">
                <Toolbar>
                    <Heading level={2}>
                        <Icon src={require('images/AI-colored.svg')} />{' '}
                        {translate('title')}
                    </Heading>
                    <ToolbarExpander />
                    <Dropdown menu={headerMenu}>
                        <Button icon="three-dots-horizontal" ghost tooltip={translate('chat-menu')} tooltipAsLabel />
                    </Dropdown>
                    <Button tooltip={translate('close-button-title')} tooltipAsLabel ghost icon="cross-mark" onClick={() => setIsRightSidebarOpen(false)} />
                </Toolbar>
            </div>
            <div className="plesk-copilot-chat-messages" ref={listRef}>
                {historyLoading && <ContentLoader />}
                {!historyLoading && answers.length === 0 ? (
                    <div className="plesk-copilot-welcome">
                        <Item
                            icon={<Icon src={require('images/AI-colored.svg')} />}
                            title={String(translate('welcome.greeting'))}
                            description={<Paragraph>{translate('welcome.description')}</Paragraph>}
                        />
                        <div className="plesk-copilot-suggestions" data-testid="copilot-suggestions">
                            <Text bold>{translate('welcome.try-asking')}</Text>
                            <div className="plesk-copilot-suggestions__labels">
                                {SUGGESTIONS.map(({ key, icon }) => (
                                    <Label
                                        key={key}
                                        icon={icon}
                                        view="outline"
                                        size="lg"
                                        onClick={() => loadAnswer(String(translate(key)))}
                                    >
                                        {translate(key)}
                                    </Label>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {answers.map(message => (
                            <MessageItem
                                key={message.id}
                                message={message}
                                onFeedback={handleFeedback}
                                feedbackDisabled={feedbackDisabled.has(message.id)}
                            />
                        ))}
                        {loading && (
                            <div className="msg-row left">
                                <div className="assistant-card">
                                    <div className="assistant-content">
                                        <ContentLoader
                                            className="plesk-copilot-loading-indicator"
                                            text={<Translate content={'loader.text'} />}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            <form
                onSubmit={onSubmit}
                className="plesk-copilot-chat-input-row"
                ref={inputRef}
            >
                <TextArea
                    value={question}
                    placeholder={String(translate('chat-input.placeholder'))}
                    disabled={loading}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey && question.trim()) {
                            e.preventDefault();
                            loadAnswer();
                        }
                    }}
                    size="fill"
                    className="plesk-copilot-chat-input"
                    autoheight
                    rows={1}
                />
                {!!question && (
                    <AiStylePicker
                        enabled={!!config?.copilot?.chat?.useStyles}
                        value={selectedStyle}
                        onChange={setSelectedStyle}
                    />
                )}
                {!!question && <Button ghost icon="arrow-right-circle-filled" type="submit" disabled={loading} />}
            </form>
            <Item
                className="plesk-copilot-privacy"
                icon={<Icon name="shield" size="16" />}
                description={
                    <Translate
                        content="privacy.text"
                        params={{
                            link: <Link href={config?.product.privacyPolicyUrl} target="_blank">{translate('privacy.link')}</Link>,
                        }}
                    />
                }
            />
        </div>
    );
};

export default Chat;
